import type { CustomDepartmentField, DepartmentCustomFieldValues } from '../types/customer/base';

/**
 * The rules for a departamento's extra fields, shared by everything that touches them.
 *
 * An empresa's departments are its tenants, contractors and sister companies, so what matters
 * about one is company data: CNPJ, nome fantasia, the responsible contact. Which of those exist
 * is the admin's call (Configurações → Campos), mirroring person fields.
 *
 * The backend applies the identical rules (condo-manager's services/department_custom_fields.go).
 * A value the browser keeps and the server drops is a field that silently empties itself on
 * save, so the two files are written to match: trim, drop blanks, drop undeclared keys, and a
 * select may only hold one of its own options.
 */

/** Declared fields, in the order they should appear in a form and on a detail screen. */
export function departmentFieldsInOrder(
  fields: CustomDepartmentField[] | undefined | null
): CustomDepartmentField[] {
  // Stable: a field added before ordering existed has no `order` and must not jump the queue.
  return (fields ?? [])
    .map((field, index) => ({ field, index }))
    .sort((a, b) => {
      const left = a.field.order ?? Number.MAX_SAFE_INTEGER;
      const right = b.field.order ?? Number.MAX_SAFE_INTEGER;
      return left === right ? a.index - b.index : left - right;
    })
    .map(entry => entry.field);
}

/** The values that may actually be stored — see the file comment for why each rule exists. */
export function departmentFieldValues(
  fields: CustomDepartmentField[] | undefined | null,
  values: DepartmentCustomFieldValues | undefined | null
): DepartmentCustomFieldValues {
  const cleaned: DepartmentCustomFieldValues = {};
  if (!fields?.length || !values) return cleaned;

  fields.forEach(field => {
    const raw = values[field.id];
    if (typeof raw !== 'string') return;
    const value = raw.trim();
    if (!value) return;
    if (field.type === 'select' && !(field.options ?? []).includes(value)) return;
    cleaned[field.id] = value;
  });
  return cleaned;
}

/** Labels of required fields with no value, for the form to show before it submits. */
export function missingRequiredDepartmentFields(
  fields: CustomDepartmentField[] | undefined | null,
  values: DepartmentCustomFieldValues | undefined | null
): string[] {
  return (fields ?? [])
    .filter(field => field.required && !(values?.[field.id] ?? '').trim())
    .map(field => field.label);
}

/**
 * A field id derived from its label, so the stored key is readable in the database and in an
 * export ("custom_nome_fantasia", not "custom_7f3a").
 */
export function newDepartmentFieldId(label: string, existing: CustomDepartmentField[]): string {
  const slug = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const base = `custom_${slug || 'campo'}`;
  const taken = new Set((existing ?? []).map(field => field.id));
  if (!taken.has(base)) return base;

  let suffix = 2;
  while (taken.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
}

export interface DepartmentFieldRow {
  id: string;
  label: string;
  value: string;
}

/** What an unfilled field reads as on the web department pages. */
export const SEM_INFORMACAO = 'Sem informação';

/**
 * What a detail screen renders: each declared field with its label, in order.
 *
 * `includeEmpty` is the difference between the web and the phones. On the web every declared
 * field is listed, unfilled ones reading "Sem informação" — hiding them made the panel
 * indistinguishable from "this empresa never declared a CNPJ field", which is precisely the
 * question somebody opening the page is asking. The phones omit them (the backend sends only
 * filled rows): six "Sem informação" lines is noise on a 5-inch screen.
 */
export function departmentFieldDisplayRows(
  fields: CustomDepartmentField[] | undefined | null,
  values: DepartmentCustomFieldValues | undefined | null,
  options?: { includeEmpty?: boolean }
): DepartmentFieldRow[] {
  return departmentFieldsInOrder(fields)
    .map(field => ({ id: field.id, label: field.label, value: (values?.[field.id] ?? '').trim() }))
    .filter(row => row.value !== '' || options?.includeEmpty)
    .map(row => (row.value === '' ? { ...row, value: SEM_INFORMACAO } : row));
}
